// ════════════════════════════════════════════════════════════════════════════
// THE ONE READ THE FRONT END MAKES — stored block text in, TYPED results out.
//
// This is the attach grammar's `parseMapBlock`. The map's own component header
// states the contract this file exists to satisfy: the block "is parsed by the
// SHARED parser (`@cabal/shared`), the same one the back end reads, so the two
// can never disagree". `parseAttachResults(raw)` answers a `ParsedAttachResults`
// whose `results` are a DISCRIMINATED UNION with named, typed fields, exactly
// as `ParsedMap.pins` are `MapPin`s. A widget renders those fields. It never
// re-parses prose, never splits a string, and never regexes a headline.
//
// ─── TWO STAGES, ONE PARSER ─────────────────────────────────────────────────
//
//   text ──parseAttachDocument──▶ generic document ──per-pillar reader──▶ typed
//
// The first stage is the grammar (bullets, indents, `key: value`, the four
// ceilings) and is pillar-blind. The second is the pillar's own result shape.
// Both live in `packages/shared` and the BL writes through their inverses, so
// there is exactly one implementation of one grammar.
//
// ─── DEGRADE TO THE WHOLE, NEVER TO A PART (D28) ────────────────────────────
//
// If the block names an adopted pillar but ANY of its items fails that
// pillar's typed read, the WHOLE block reads generic. One block holds one
// search, and one search's rows read as one kind of thing — the same call the
// BL's projector registry makes when a projector answers null. A card with a
// hole in it is worse than an honest list.
//
// ─── EVERY ROW-MINTING PILLAR IS ADOPTED ─────────────────────────────────────
//
// Hotels, Flights, Web Search, Workouts and Nutrition have semantic codecs.
// The other twenty-two projector formats are exact string-valued shapes generated
// from their backend field enums. Only Weather has no shape because it mints
// no rows. Generic remains the forward-compatible read for an
// unknown pillar or a block whose fields a newer writer changed.
//
// Workouts and Nutrition earned codecs rather than string bags because their
// records are not flat: a session's slot carries a prescription a card draws as
// three separate figures, and a food's serving carries fourteen nutrients under
// one measure. A string-valued reader would hand those to a widget as display
// lines, and the widget would have to split them — which is the prose-regexing
// this seam exists to forbid.
// ════════════════════════════════════════════════════════════════════════════

import { PillarCode } from "../../session/orchestration/tool-protocol/follow-up-refs.js";
import {
  ATTACH_FENCE_INFO_STRING,
  AttachLinkFieldKey,
  type AttachFieldLine,
} from "../grammar.js";
import { fenceSpansOfKind, type FenceSpan } from "../fence-spans.js";
import {
  attachFieldValue,
  parseAttachDocument,
  type ParsedAttachDocument,
  type ParsedAttachItem,
} from "../parse-attach-document.js";
import {
  readFlightsAttachItem,
  type FlightsAttachResult,
} from "./flights.shape.js";
import {
  readHotelsAttachItem,
  type HotelsAttachResult,
} from "./hotels.shape.js";
import {
  readNutritionAttachItem,
  type NutritionAttachResult,
} from "./nutrition.shape.js";
import {
  readWorkoutsAttachItem,
  type WorkoutsAttachResult,
} from "./workouts.shape.js";
import {
  readWebSearchAttachItem,
  type WebSearchAttachResult,
} from "./web-search.shape.js";
import {
  AUTHORED_ATTACH_SHAPE_BY_PILLAR,
  type AuthoredAttachShape,
} from "./authored-fields.generated.js";
import {
  isAuthoredAttachPillar,
  readAuthoredAttachItem,
  type AuthoredAttachResult,
} from "./authored.shape.js";

/**
 * Which typed shape a block's items are read as.
 *
 * The discriminator, and it is the pillar's own name wherever a pillar has
 * been adopted — the same spelling `kind:` prints — plus `generic` for every
 * pillar whose shape has not been authored yet. `MapPin.kind` is the
 * precedent: one field, a closed set, and the consumer narrows on it.
 */
export enum AttachResultShape {
  Hotels = "hotels",
  Flights = "flights",
  WebSearch = "web-search",
  Workouts = "workouts",
  Nutrition = "nutrition",
  Generic = "generic",
}

/** Every discriminator the shared read can answer. The five semantic codecs
 * use `AttachResultShape`; the twenty-two exact string-valued readers use their
 * pillar name, generated from the backend writer registry. */
export type AttachResultShapeName = AttachResultShape | AuthoredAttachShape;

/**
 * One item that could not be read through a known shape — the item as the
 * GRAMMAR parsed it, plus the three keys the generic projector writes.
 *
 * `fields` is carried whole and deliberately: it is the only arm where a
 * consumer legitimately walks untyped pairs, because nobody has yet said what
 * this pillar's data means. Every adopted arm answers named fields instead.
 */
export interface GenericAttachResult {
  readonly headline: string;
  readonly fields: readonly AttachFieldLine[];
  readonly linkUrl: string | null;
  readonly description: string | null;
  readonly photoUrl: string | null;
}

/** The COMMON per-result metadata every arm of the union carries: the block
 *  writer's own `[label](url)` comment on this one item, lifted by stage-one
 *  parsing (`ParsedAttachItem.writerLabel`), or null. It is a property OF the
 *  union, not of any pillar's vocabulary — which is what lets a card render it
 *  as the writer's comment without a single pillar reader knowing it exists. */
export interface AttachWriterLabelled {
  readonly writerLabel: string | null;
}

/**
 * One result, typed and discriminated — the attach grammar's `MapPin`.
 *
 * Flat rather than nested (`{ shape, ...fields }`, not `{ shape, result }`)
 * for the same reason `MapPinCoordinates` carries `lat` and `lon` at the top
 * level: a narrow on `shape` should hand a component its fields, not a box to
 * open first. Every arm — authored AND generic — carries the common
 * `writerLabel`, so a consumer reads it before narrowing.
 */
export type AttachResult = (
  | ({ readonly shape: AttachResultShape.Hotels } & HotelsAttachResult)
  | ({ readonly shape: AttachResultShape.Flights } & FlightsAttachResult)
  | ({ readonly shape: AttachResultShape.WebSearch } & WebSearchAttachResult)
  | ({ readonly shape: AttachResultShape.Workouts } & WorkoutsAttachResult)
  | ({ readonly shape: AttachResultShape.Nutrition } & NutritionAttachResult)
  | AuthoredAttachResult
  | ({ readonly shape: AttachResultShape.Generic } & GenericAttachResult)
) &
  AttachWriterLabelled;

/** One stored block, read as typed results. The block-level facts are the
 *  document parser's, unchanged; `results` is what the re-cut added. */
export interface ParsedAttachResults {
  /** The capture datetime exactly as the caption states it, or null. */
  readonly capturedAtLabel: string | null;
  /** The pillar the `kind:` directive names, or null when this build does not
   *  know the name — which is not an error and never a guess. */
  readonly pillar: PillarCode | null;
  readonly about: string | null;
  readonly results: readonly AttachResult[];
  /** Which shape every result in this block was read as. One block, one
   *  shape — see the degrade rule. */
  readonly shape: AttachResultShapeName;
  readonly droppedItemCount: number;
  readonly droppedFieldCount: number;
  readonly unknownDirectiveCount: number;
}

/** One located block and the typed results it holds. */
export interface LocatedAttachResults {
  readonly span: FenceSpan;
  readonly parsed: ParsedAttachResults;
}

/**
 * The row-minting pillars and their executable shape discriminator. `Partial`
 * leaves Weather absent by construction: it cannot mint a block.
 */
export const ATTACH_SHAPE_BY_PILLAR: Partial<
  Record<PillarCode, AttachResultShapeName>
> = {
  ...AUTHORED_ATTACH_SHAPE_BY_PILLAR,
  [PillarCode.Hotels]: AttachResultShape.Hotels,
  [PillarCode.Flights]: AttachResultShape.Flights,
  [PillarCode.WebSearch]: AttachResultShape.WebSearch,
  [PillarCode.Workouts]: AttachResultShape.Workouts,
  [PillarCode.Nutrition]: AttachResultShape.Nutrition,
};

/** The shape a block from `pillar` is read as — generic where the pillar has
 *  no authored shape, and where the block names no pillar this build knows. */
export function attachShapeFor(input: {
  pillar: PillarCode | null;
}): AttachResultShapeName {
  if (input.pillar === null) return AttachResultShape.Generic;
  return ATTACH_SHAPE_BY_PILLAR[input.pillar] ?? AttachResultShape.Generic;
}

function genericResultOf(input: { item: ParsedAttachItem }): AttachResult {
  const item = input.item;
  return {
    shape: AttachResultShape.Generic,
    headline: item.headline,
    fields: item.fields,
    linkUrl: attachFieldValue({ item, key: AttachLinkFieldKey.Link }),
    description: attachFieldValue({
      item,
      key: AttachLinkFieldKey.Description,
    }),
    photoUrl: attachFieldValue({ item, key: AttachLinkFieldKey.Photo }),
    writerLabel: item.writerLabel,
  };
}

/**
 * Every item read through one pillar's shape, or null when ANY item refused —
 * the whole-block degrade (D28). Null here is not a failure of the block: it
 * is the block reading generic, which is what it did before the pillar was
 * adopted and what it will do again if a future deploy writes an item this
 * build cannot type.
 */
function typedResultsOf(input: {
  pillar: PillarCode;
  shape: AttachResultShapeName;
  items: readonly ParsedAttachItem[];
}): AttachResult[] | null {
  const results: AttachResult[] = [];
  for (const item of input.items) {
    // The common metadata joins AFTER the strict read, from the property
    // stage-one parsing lifted — the reader itself never meets the reserved
    // key, which is what keeps a labelled item from downgrading the block.
    const writerLabel = item.writerLabel;
    if (isAuthoredAttachPillar(input.pillar)) {
      const result = readAuthoredAttachItem({ pillar: input.pillar, item });
      if (result === null) return null;
      results.push({ ...result, writerLabel });
      continue;
    }
    if (input.shape === AttachResultShape.Hotels) {
      const result = readHotelsAttachItem({ item });
      if (result === null) return null;
      results.push({ shape: AttachResultShape.Hotels, ...result, writerLabel });
      continue;
    }
    if (input.shape === AttachResultShape.Flights) {
      const result = readFlightsAttachItem({ item });
      if (result === null) return null;
      results.push({ shape: AttachResultShape.Flights, ...result, writerLabel });
      continue;
    }
    if (input.shape === AttachResultShape.WebSearch) {
      const result = readWebSearchAttachItem({ item });
      if (result === null) return null;
      results.push({
        shape: AttachResultShape.WebSearch,
        ...result,
        writerLabel,
      });
      continue;
    }
    if (input.shape === AttachResultShape.Workouts) {
      const result = readWorkoutsAttachItem({ item });
      if (result === null) return null;
      results.push({
        shape: AttachResultShape.Workouts,
        ...result,
        writerLabel,
      });
      continue;
    }
    if (input.shape === AttachResultShape.Nutrition) {
      const result = readNutritionAttachItem({ item });
      if (result === null) return null;
      results.push({
        shape: AttachResultShape.Nutrition,
        ...result,
        writerLabel,
      });
      continue;
    }
    return null;
  }
  return results;
}

/** One parsed document, read as typed results. Split out from
 *  `parseAttachResults` so a consumer that already holds a
 *  `ParsedAttachDocument` — the FE's current card prop — can move onto the
 *  typed read without re-parsing the text. */
export function readAttachResults(input: {
  parsed: ParsedAttachDocument;
}): ParsedAttachResults {
  const parsed = input.parsed;
  const shape = attachShapeFor({ pillar: parsed.pillar });
  const typed =
    shape === AttachResultShape.Generic || parsed.pillar === null
      ? null
      : typedResultsOf({ pillar: parsed.pillar, shape, items: parsed.items });
  return {
    capturedAtLabel: parsed.capturedAtLabel,
    pillar: parsed.pillar,
    about: parsed.about,
    shape: typed === null ? AttachResultShape.Generic : shape,
    results: typed ?? parsed.items.map((item) => genericResultOf({ item })),
    droppedItemCount: parsed.droppedItemCount,
    droppedFieldCount: parsed.droppedFieldCount,
    unknownDirectiveCount: parsed.unknownDirectiveCount,
  };
}

/**
 * One `no-edit-data-block` body, read straight to typed results.
 *
 * THE FRONT END'S WHOLE READ. `parseMapBlock(raw)` is the precedent and the
 * shape of the call is deliberately the same: text in, a typed domain object
 * out, null when the block holds nothing to render.
 */
export function parseAttachResults(raw: string): ParsedAttachResults | null {
  const parsed = parseAttachDocument(raw);
  return parsed === null ? null : readAttachResults({ parsed });
}

/** Every persisted attach block in a document, located and read as typed
 *  results, in document order. */
export function locateAttachResults(input: {
  markdown: string;
}): LocatedAttachResults[] {
  return fenceSpansOfKind({
    markdown: input.markdown,
    info: ATTACH_FENCE_INFO_STRING,
  }).flatMap((span) => {
    const parsed = parseAttachResults(span.body);
    return parsed === null ? [] : [{ span, parsed }];
  });
}
